import type { UploadKind } from '@/utils/api/uploads/kinds';
import { MAX_UPLOADS_PER_REQUEST } from '@/utils/api/upload-schemas';

type Minted = { upload_id: string; url: string; headers: Record<string, string> };

export type StagedFile = {
  uploadId: string;
  /** Cancels the PUT; `done` then rejects with an AbortError. */
  abort: () => void;
  done: Promise<void>;
};

/** Upload URLs for several files in one request. Throws with the server's message. */
async function mintUploads(entries: { file: File; kind: UploadKind }[]): Promise<Minted[]> {
  const res = await fetch('/api/uploads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      uploads: entries.map(({ file, kind }) => ({ kind, filename: file.name, size_bytes: file.size })),
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error ?? 'Could not start the upload');
  return (body as { uploads: Minted[] }).uploads;
}

/** Waits before the 2nd and 3rd PUT; the presigned URL lives an hour, so a re-PUT reuses it. */
export const PUT_RETRY_DELAYS_MS = [1_000, 3_000] as const;

type PutOutcome = { ok: true } | { ok: false; error: Error; retryable: boolean };

/** Network errors and 5xx are worth another try; 4xx (signature, size, expiry) never succeed on repeat. */
export function isRetryablePut(status: number | 'network'): boolean {
  return status === 'network' || status >= 500;
}

/** PUTs a file to its presigned URL, retrying transient failures. XHR, because fetch reports no upload progress. */
function putFile(minted: Minted, file: File, onProgress?: (percent: number) => void): StagedFile {
  let xhr: XMLHttpRequest | null = null;
  let aborted = false;
  let wake: (() => void) | null = null;

  const attempt = () =>
    new Promise<PutOutcome>((resolve) => {
      const req = new XMLHttpRequest();
      xhr = req;
      req.open('PUT', minted.url);
      for (const [name, value] of Object.entries(minted.headers)) {
        // Browsers set Content-Length from the body and refuse to let scripts set it.
        if (name.toLowerCase() !== 'content-length') req.setRequestHeader(name, value);
      }
      req.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100));
      };
      req.onload = () =>
        resolve(
          req.status >= 200 && req.status < 300
            ? { ok: true }
            : { ok: false, error: new Error(`Upload failed (${req.status})`), retryable: isRetryablePut(req.status) }
        );
      req.onerror = () => resolve({ ok: false, error: new Error('Upload failed'), retryable: isRetryablePut('network') });
      req.onabort = () => resolve({ ok: false, error: new DOMException('Upload cancelled', 'AbortError'), retryable: false });
      req.send(file);
    });

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, ms);
      wake = () => {
        clearTimeout(timer);
        resolve();
      };
    });

  const done = (async () => {
    for (let i = 0; ; i++) {
      const outcome = await attempt();
      if (outcome.ok) return;
      if (aborted) throw new DOMException('Upload cancelled', 'AbortError');
      if (!outcome.retryable || i >= PUT_RETRY_DELAYS_MS.length) throw outcome.error;
      await sleep(PUT_RETRY_DELAYS_MS[i]);
      if (aborted) throw new DOMException('Upload cancelled', 'AbortError');
      onProgress?.(0);
    }
  })();

  const abort = () => {
    aborted = true;
    xhr?.abort();
    wake?.();
  };
  return { uploadId: minted.upload_id, abort, done };
}

/** Starts one upload. Await `done` before handing `uploadId` to the route that consumes it. */
export async function stageFile(file: File, kind: UploadKind, onProgress?: (percent: number) => void): Promise<StagedFile> {
  const [minted] = await mintUploads([{ file, kind }]);
  return putFile(minted, file, onProgress);
}

export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Starts several uploads, minting in batches the route accepts, each with its own progress. */
export async function stageFiles(
  entries: { file: File; kind: UploadKind; onProgress?: (percent: number) => void }[]
): Promise<StagedFile[]> {
  const minted: Minted[] = [];
  for (const batch of chunk(entries, MAX_UPLOADS_PER_REQUEST)) {
    minted.push(...(await mintUploads(batch)));
  }
  return entries.map((entry, i) => putFile(minted[i], entry.file, entry.onProgress));
}

export type SettledUploads<T> = {
  uploaded: { item: T; uploadId: string }[];
  failed: { item: T; reason: string }[];
};

/** Splits settled PUTs so one failure doesn't discard the files that landed. */
export function partitionSettled<T>(
  items: T[],
  staged: Pick<StagedFile, 'uploadId'>[],
  results: PromiseSettledResult<void>[]
): SettledUploads<T> {
  const out: SettledUploads<T> = { uploaded: [], failed: [] };
  results.forEach((result, i) => {
    if (result.status === 'fulfilled') {
      out.uploaded.push({ item: items[i], uploadId: staged[i].uploadId });
    } else {
      const reason = result.reason instanceof Error ? result.reason.message : 'Upload failed';
      out.failed.push({ item: items[i], reason });
    }
  });
  return out;
}
